import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, api } from "../api.ts";
import {
  Badge,
  ButtonLink,
  Card,
  cx,
  Empty,
  ErrorText,
  Loading,
  Page,
  Segmented,
  Select,
} from "../ui.tsx";

interface Event {
  id: string;
  at: string;
  company: { id: string; name: string } | null;
  who: string;
  text: string;
  attention: boolean;
}

type Range = "1" | "7" | "30";
type Show = "all" | "attention";

/** In the viewer's own time zone: on a phone, "3:42 PM" beats UTC. */
const time = (at: string) =>
  new Date(at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

function dayName(at: string): string {
  const d = new Date(at);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 864e5);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

/**
 * Added to a home screen from this page, ninekeep opens here ("ninekeep team"), not on the
 * customer home page: the page points the browser at team.webmanifest while it's shown.
 */
function useTeamApp() {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const title = document.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]',
    );
    if (!link) return;
    const before = { href: link.getAttribute("href"), title: title?.content };
    link.setAttribute("href", "/team.webmanifest");
    if (title) title.content = "ninekeep team";
    return () => {
      if (before.href) link.setAttribute("href", before.href);
      if (title && before.title) title.content = before.title;
    };
  }, []);
}

/**
 * ninekeep's own team: what every customer did lately, newest first, made to check on a phone.
 * Refreshes every minute and whenever the app comes back to the front.
 */
export function ActivityAdmin() {
  useTeamApp();
  const [range, setRange] = useState<Range>("7");
  const [show, setShow] = useState<Show>("all");
  const [company, setCompany] = useState("");
  const q = useQuery({
    queryKey: ["operator-activity", range],
    queryFn: () =>
      api.get<{ events: Event[]; companies: number }>(`/operator/activity?days=${range}`),
    refetchInterval: 60_000,
  });
  if (q.isPending) return <Loading />;
  // Anyone outside ninekeep's team gets the same page as any unknown address.
  if (q.error instanceof ApiError && q.error.status === 404)
    return (
      <Page title="Page not found">
        <Card>
          <Empty title="There's no page at this address.">
            <div className="mt-3 flex justify-center">
              <ButtonLink to="/">Go to Home</ButtonLink>
            </div>
          </Empty>
        </Card>
      </Page>
    );
  if (q.isError)
    return (
      <Page title="Activity">
        <ErrorText error={q.error} />
      </Page>
    );
  const all = q.data.events;
  const names = [
    ...new Map(all.filter((e) => e.company).map((e) => [e.company!.id, e.company!.name])),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const events = all.filter(
    (e) => (show === "all" || e.attention) && (!company || e.company?.id === company),
  );
  const count = (s: string) => all.filter((e) => e.text.startsWith(s)).length;
  const attention = all.filter((e) => e.attention).length;
  const days: [string, Event[]][] = [];
  for (const e of events) {
    const name = dayName(e.at);
    const last = days[days.length - 1];
    if (last && last[0] === name) last[1].push(e);
    else days.push([name, [e]]);
  }
  return (
    <Page
      title="Activity"
      lead="What every customer did lately, newest first. Only ninekeep's team sees this page."
    >
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="How far back"
          value={range}
          onChange={setRange}
          options={[
            { id: "1", label: "Today" },
            { id: "7", label: "7 days" },
            { id: "30", label: "30 days" },
          ]}
        />
        <Segmented
          label="What to show"
          value={show}
          onChange={setShow}
          options={[
            { id: "all", label: "Everything" },
            { id: "attention", label: `Needs a look${attention ? ` (${attention})` : ""}` },
          ]}
        />
        {names.length > 1 && (
          <Select
            aria-label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-auto"
          >
            <option value="">Every company</option>
            {names.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>
        )}
      </div>
      {/* One short row, so the events start on a phone's first screen. */}
      <Card flush>
        <dl className="grid grid-cols-4 divide-x divide-line">
          {(
            [
              ["Companies", q.data.companies],
              ["Uploaded", count("Uploaded")],
              ["Read", count("ninekeep read")],
              ["Need a look", attention],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="px-2 py-3 text-center">
              <dt className="text-xs text-muted">{label}</dt>
              <dd
                className={cx(
                  "tabular font-display text-xl font-bold",
                  label === "Need a look" && value > 0 && "text-danger",
                )}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </Card>
      {days.length === 0 ? (
        <Card>
          <Empty title={show === "attention" ? "Nothing needs a look." : "Nothing happened yet."}>
            Sign-ups, uploads, readings, approvals and claims show up here as they happen.
          </Empty>
        </Card>
      ) : (
        days.map(([day, list]) => (
          <Card key={day} title={day} flush>
            <ul className="divide-y divide-line">
              {list.map((e) => (
                <li key={e.id} className="flex gap-3 px-4 py-3 sm:px-5">
                  <span className="tabular w-16 shrink-0 pt-0.5 text-sm text-muted">
                    {time(e.at)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium break-words">{e.text}</span>
                      {e.attention && <Badge tone="danger">Needs a look</Badge>}
                    </div>
                    <div className="text-sm break-words text-muted">
                      {[e.company?.name ?? "ninekeep", e.who].join(" · ")}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ))
      )}
    </Page>
  );
}
